<script setup>
import { computed, onMounted, onUpdated, ref } from "vue";

const props = defineProps({
  position: {
    type: String,
    default: "start",
  },
  textPrimary: {
    type: [String, Number],
    default: "",
  },
  textSecondary: {
    type: [String, Number],
    default: "",
  },
  textThird: {
    type: [String, Number],
    default: "",
  },
  maxWidth: {
    type: String,
    default: "",
  },
  hasHide: {
    type: Boolean,
    default: false,
  },
});


function resolveTooltipValue() {
  if (props.textSecondary) return props.textSecondary;
  if (props.textPrimary) return props.textPrimary;
  return "";
}

const textContainerRef = ref(null);
const showTooltip = ref(false);

function isElementTruncated(element) {
  if (!element) return false;
  return element.scrollWidth > element.clientWidth;
}

function updateTooltipVisibility() {
  if (props.maxWidth === "") {
    showTooltip.value = false;
    return;
  }

  const container = textContainerRef.value;
  if (!container) {
    showTooltip.value = false;
    return;
  }

  const textRows = Array.from(container.querySelectorAll("p"));
  if (textRows.length === 0) {
    showTooltip.value = isElementTruncated(container);
    return;
  }

  showTooltip.value = textRows.some((row) => isElementTruncated(row));
}

function queueTooltipVisibilityUpdate() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      updateTooltipVisibility();
    });
  });
}

onMounted(() => {
  queueTooltipVisibilityUpdate();
});

onUpdated(() => {
  queueTooltipVisibilityUpdate();
});

function getClass(position, textSecondary) {
  let pos = `text-${position}`;
  let align = textSecondary != "" ? "align-middle" : "";
  return `${pos} ${align}`;
}

function getStyle(maxWidth) {
  return maxWidth
    ? `max-width: ${maxWidth} !important; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;`
    : "";
}

const tooltipText = computed(() => {
  if (props.maxWidth === "" || !showTooltip.value) return "";
  return resolveTooltipValue();
});
</script>

<template>
  <td :class="getClass(position, textSecondary)" :style="getStyle(maxWidth)">
    <div class="td-content copy-value-container">
      <div v-if="$slots.prepend" class="td-prepend">
        <slot name="prepend"></slot>
      </div>
      <div
        ref="textContainerRef"
        v-tooltip="tooltipText"
        class="d-flex flex-column justify-content-center td-text"
        :class="{ 'text-truncate': showTooltip }"
      >
        <p v-if="textPrimary" class="text-xs mb-0 td-text-primary">
          {{ textPrimary }}
        </p>
        <p
          v-if="textSecondary !== ''"
          :class="{ 'password-text': hasHide }"
          class="text-xs mb-0 td-text-secondary"
        >
          {{ textSecondary }}
        </p>
        <p v-if="textThird !== ''" class="text-xs mb-0 td-text-secondary">
          {{ textThird }}
        </p>
      </div>
      <div v-if="$slots.append" class="td-append">
        <slot name="append"></slot>
      </div>
    </div>
    <slot></slot>
  </td>
</template>

<style lang="scss" scoped>
.td-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.td-prepend {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
}

.td-append {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  margin-left: -0.3rem;
}

.td-text {
  min-width: 0;
}

.td-text-primary {
  color: var(--color-text-primary);
  font-weight: 500;
}

.td-text-secondary {
  color: var(--color-text-secondary);
  font-weight: 500;
}

.password-text {
  -webkit-text-security: disc;
  text-security: disc;
}
.text-truncate {
  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
